const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function extractFilename(contentDisposition?: string): string | null {
  if (!contentDisposition) {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, '');
    }
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1]?.trim() || null;
}

function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error('读取导出文件失败'));
    reader.readAsArrayBuffer(blob);
  });
}

function readBlobAsText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('读取导出错误信息失败'));
    reader.readAsText(blob);
  });
}

async function blobStartsWithZipMagic(blob: Blob): Promise<boolean> {
  const bytes = new Uint8Array(await readBlobAsArrayBuffer(blob.slice(0, 4)));
  return bytes[0] === 0x50 && bytes[1] === 0x4b;
}

async function parseBlobError(blob: Blob): Promise<unknown> {
  const text = await readBlobAsText(blob);
  const trimmed = text.trim();
  if (!trimmed) {
    return '导出失败：服务端返回了空文件';
  }
  if (trimmed === '[object Object]') {
    return '导出失败：服务端未返回有效的 Excel 文件';
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

export async function assertExcelBlob(blob: Blob): Promise<void> {
  const isExcelMime = blob.type === XLSX_MIME || blob.type === 'application/octet-stream';
  const hasZipMagic = await blobStartsWithZipMagic(blob);
  if (isExcelMime && hasZipMagic) {
    return;
  }

  throw await parseBlobError(blob);
}

export async function saveExcelResponse(response: unknown, fallbackFilename: string): Promise<void> {
  if (!isRecord(response)) {
    throw new Error('导出失败：响应格式异常');
  }

  const data = response.data;
  const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: XLSX_MIME });
  await assertExcelBlob(blob);

  const headers = isRecord(response.headers) ? response.headers : {};
  const disposition = String(headers['content-disposition'] || headers['Content-Disposition'] || '');
  const filename = extractFilename(disposition) || fallbackFilename;

  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}
